import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { search } from '../../api/searchApi';
import SearchFilters from './SearchFilters';
import SearchResultCard from './../SearchResultCard';
import Loader from '../common/Loader';
import './SearchResults.css';


const SearchResults = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [parsed, setParsed] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { query, category, location: searchLocation } = location.state || {};

  // The search bar's location always applies unless the user overrides it in the sidebar
  const effectiveFilters = filters.location
    ? filters
    : (searchLocation ? { ...filters, location: searchLocation } : filters);

  useEffect(() => {
    if (query || searchLocation) {
      performSearch();
    }
  }, [query, category, searchLocation, filters, location.key]);

  const performSearch = async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await search(query, category, effectiveFilters, page, 12);
      
      if (response.success) {
        setResults(response.data);
        setPagination(response.pagination);
        setParsed(response.parsed || null);
        console.log('Search results:', response.data);
      } else {
        setError(response.error);
        console.error('Search failed:', response.error);
      }
    } catch (err) {
      const errorMsg = err.message || 'Search failed';
      setError(errorMsg);
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handlePageChange = (newPage) => {
    performSearch(newPage);
    window.scrollTo(0, 0);
  };

  if (!query && !searchLocation) {
    return (
      <div className="search-results-container">
        <div className="no-query">
          <i className="fa fa-search"></i>
          <h2>Start your search</h2>
          <p>Enter a search term or a location to find properties, hotels, and services</p>
        </div>
      </div>
    );
  }

 return (
    <div className="search-results-container">
      <div className="search-header">
        <div className="search-title-section">
          <h1>
            <i className="fas fa-search"></i>
            {query ? `Search Results for "${query}"` : 'Search Results'}
            {(searchLocation || parsed?.location) && <span className="category-tag"> near {searchLocation || parsed.location}</span>}
            {(category !== 'all' || (parsed?.category && parsed.category !== 'all')) && <span className="category-tag"> in {category !== 'all' ? category : parsed.category}</span>}
          </h1>
          
          {pagination.total !== undefined && (
            <p className="results-count">
              {pagination.total} {pagination.total === 1 ? 'result' : 'results'} found
            </p>
          )}
        </div>
        
        <button 
          className="filter-toggle-btn"
          onClick={() => setShowFilters(!showFilters)}
        >
          <i className={`fas ${showFilters ? 'fa-times' : 'fa-filter'}`}></i>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      <div className="search-content">
        {showFilters && (
          <div className="filters-sidebar">
            <SearchFilters 
              category={category !== 'all' ? category : (parsed?.category || 'all')}
              onFilterChange={handleFilterChange}
              currentFilters={effectiveFilters}
            />
          </div>
        )}

        <div className="results-main">
          {loading && <Loader />}
          
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              <h3>Search Error</h3>
              <p>{error}</p>
              <button onClick={() => performSearch()} className="retry-btn">
                <i className="fas fa-redo"></i> Try Again
              </button>
            </div>
          )}

          {!loading && !error && results.length === 0 && (
            <div className="no-results">
              <div className="no-results-icon">
                <i className="fas fa-search"></i>
              </div>
              <h3>No results found</h3>
              <p>Try adjusting your search criteria or filters</p>
              <button onClick={() => navigate('/')} className="back-home-btn">
                <i className="fas fa-home"></i> Back to Home
              </button>
            </div>
          )}

          {!loading && !error && results.length > 0 && (
            <>
              <div className="results-grid">
                {results.map((result) => (
                  <SearchResultCard 
                    key={`${result.type}-${result.id}`} 
                    result={result} 
                  />
                ))}
              </div>

              {pagination.pages > 1 && (
                <div className="pagination">
                  <button
                    className="page-btn prev-next"
                    onClick={() => handlePageChange(pagination.current - 1)}
                    disabled={pagination.current === 1}
                  >
                    <i className="fas fa-chevron-left"></i> Previous
                  </button>
                  
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.current <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.current >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.current - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        className={`page-btn ${pagination.current === pageNum ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  {pagination.pages > 5 && pagination.current < pagination.pages - 2 && (
                    <span className="page-dots">...</span>
                  )}
                  
                  {pagination.pages > 5 && pagination.current < pagination.pages - 2 && (
                    <button
                      className="page-btn"
                      onClick={() => handlePageChange(pagination.pages)}
                    >
                      {pagination.pages}
                    </button>
                  )}
                  
                  <button
                    className="page-btn prev-next"
                    onClick={() => handlePageChange(pagination.current + 1)}
                    disabled={pagination.current === pagination.pages}
                  >
                    Next <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;